import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import Peer from "peerjs";

export default function VideoCall() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const socket = useRef(null);
  const peer = useRef(null);
  const videoGrid = useRef();
  const localVideo = useRef();
  const localStream = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    socket.current = io("http://localhost:4000");
    peer.current = new Peer(undefined, {
      path: "/peerjs/myapp",
      host: "/",
      port: "4000",
    });

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        localStream.current = stream;
        localVideo.current.srcObject = stream;
        localVideo.current.muted = true;
        localVideo.current.addEventListener("loadedmetadata", () => {
          localVideo.current.play();
        });

        peer.current.on("call", (call) => {
          call.answer(stream);
          const video = document.createElement("video");
          video.className =
            "w-full h-full object-cover rounded-xl border border-gray-700 shadow-lg";
          call.on("stream", (userVideoStream) => {
            addVideoStream(video, userVideoStream);
          });
        });

        socket.current.on("user-connected", (userId) => {
          connectToNewUser(userId, stream);
        });
      });

    peer.current.on("open", (id) => {
      socket.current.emit("join-room", roomId, id);
    });

    function connectToNewUser(userId, stream) {
      const call = peer.current.call(userId, stream);
      const video = document.createElement("video");
      video.className =
        "w-full h-full object-cover rounded-xl border border-gray-700 shadow-lg";
      call.on("stream", (userVideoStream) => {
        addVideoStream(video, userVideoStream);
      });
      call.on("close", () => {
        video.remove();
      });
    }

    function addVideoStream(video, stream) {
      video.srcObject = stream;
      video.addEventListener("loadedmetadata", () => {
        video.play();
      });
      videoGrid.current.innerHTML = "";
      videoGrid.current.append(video);
    }

    return () => {
      if (socket.current) socket.current.disconnect();
      if (peer.current) peer.current.destroy();
      if (localStream.current)
        localStream.current.getTracks().forEach((track) => track.stop());
    };
  }, [roomId]);

  const toggleMute = () => {
    if (!localStream.current) return;
    localStream.current.getAudioTracks().forEach((track) => {
      track.enabled = !track.enabled;
    });
    setIsMuted(!isMuted);
  };

  const endCall = () => {
    if (localStream.current)
      localStream.current.getTracks().forEach((track) => track.stop());
    if (peer.current) peer.current.destroy();
    if (socket.current) socket.current.disconnect();
    navigate("/"); // Redirect to home
  };

  return (
    <div className="relative w-screen h-screen bg-black overflow-hidden flex items-center justify-center">
      <div
        ref={videoGrid}
        className="w-[85vw] h-[80vh] bg-gray-900 rounded-2xl shadow-2xl flex items-center justify-center border border-gray-700"
      >
        <p className="text-gray-500 text-lg">Waiting for patient...</p>
      </div>

      <video
        ref={localVideo}
        className="absolute bottom-6 right-6 w-[220px] h-[160px] object-cover rounded-xl border-2 border-white shadow-lg"
      ></video>

      <div className="absolute top-4 left-6 text-white text-lg font-semibold">
        Doctor’s Consultation Room
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4">
        <button
          onClick={toggleMute}
          className={`px-6 py-2 rounded-full font-semibold text-white shadow-lg transition ${
            isMuted ? "bg-gray-600" : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {isMuted ? "Unmute" : "Mute"}
        </button>

        <button
          onClick={endCall}
          className="px-6 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold shadow-lg"
        >
          End Call
        </button>
      </div>
    </div>
  );
}
